/**
 * My account (container, Phase 7b): who I am and my offline PIN (D20) — set,
 * change or clear it. Setting needs `local.login` somewhere (a node re-checks
 * it for its own site). The PIN is the cloud's: from a node's page it is set there directly (host-aware routing), and
 * the card says why when the cloud cannot be reached.
 */
import { toast } from 'sonner';
import { useGrants, useOfflinePin, useOfflinePinWrite, useRouteAvailability, useSession } from '../app/hooks.js';
import { unavailableText } from '../connectivity/routing.js';
import { OfflinePinCard } from '../components/admin/OfflinePinCard.js';
import { Card, CardDescription, CardHeader, CardTitle } from '../components/ui/card.js';
import { allowsSomewhere } from '../lib/permissions.js';
import { errorText } from './admin/common.js';

export function AccountView() {
  const session = useSession();
  const grants = useGrants();
  // The PIN is the cloud's (D20): from a node's page it is set there directly, unless the cloud cannot be reached.
  const pinRoute = useRouteAvailability('getOfflinePin');
  const pin = useOfflinePin();
  const write = useOfflinePinWrite();
  return (
    <div className="mx-auto grid max-w-3xl gap-4" data-testid="account-page">
      <Card>
        <CardHeader>
          <CardTitle>My account</CardTitle>
          <CardDescription>
            Signed in as {session?.email ?? session?.userId ?? '?'}
            {session?.tenantId === null || session?.tenantId === undefined ? '' : ` in ${session.tenantId}`}.
          </CardDescription>
        </CardHeader>
      </Card>
      {!pinRoute.ok ? (
        <Card data-testid="pin-cloud-only" data-reason={pinRoute.reason}>
          <CardHeader>
            <CardTitle>Offline PIN</CardTitle>
            <CardDescription>
              Your offline PIN needs a cloud connection. {unavailableText(pinRoute.reason)} A PIN set in the cloud reaches this server with its next directory sync.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <OfflinePinCard
          status={pin.data ?? null}
          canSet={allowsSomewhere(grants, 'local.login')}
          pending={write.isPending}
          error={errorText(pin.error ?? write.error)}
          onSet={(value) => write.mutate({ pin: value }, { onSuccess: () => toast.success('Offline PIN set') })}
          onClear={() => write.mutate({ pin: null }, { onSuccess: () => toast.success('Offline PIN cleared') })}
        />
      )}
    </div>
  );
}
